import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BlobField from '../components/BlobField';
import ImpersonationBanner from '../components/ImpersonationBanner';
import api, { formatApiErrorDetail } from '../api';
import { useAuth } from '../context/AuthContext';
import { COUNTRIES, COUNTRY_BY_CODE, countryFlag } from '../lib/countries';

/**
 * 02 — Profile Setup (matches 02-profile-setup.html).
 * Fields: avatar, display name, handle (with @-prefix + availability check),
 * short bio (0/120), category chips (pick up to 3).
 */
const CATEGORY_FALLBACK = [
  { key: 'Trader', color: 'c-orange' },
  { key: 'Content Creator', color: 'c-pink' },
  { key: 'Coach', color: 'c-blue' },
  { key: 'Educator', color: 'c-green' },
  { key: 'Community Builder', color: 'c-purple' },
  { key: 'Influencer', color: 'c-yellow' },
];

export default function ProfileSetup() {
  const navigate = useNavigate();
  const { user, refreshMe } = useAuth();

  const [displayName, setDisplayName] = useState('');
  const [handle, setHandle] = useState('');
  const [bio, setBio] = useState('');
  const [categories, setCategories] = useState([]);
  const [avatar, setAvatar] = useState(null); // data URL
  const [handleCheck, setHandleCheck] = useState({ state: 'idle', message: '' });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [CATEGORIES, setCategoryList] = useState(CATEGORY_FALLBACK);
  // Phone capture (Feb 2026) — not required, no OTP. Country dropdown
  // + free-text phone field. Empty values are accepted and skipped
  // server-side.
  const [phoneCountry, setPhoneCountry] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  useEffect(() => {
    api.get('/categories')
      .then(({ data }) => {
        if (Array.isArray(data.categories) && data.categories.length) {
          setCategoryList(data.categories.map(c => ({ key: c.key, color: c.color, label: c.label })));
        }
      })
      .catch(() => {}); // keep fallback if fetch fails
  }, []);

  useEffect(() => {
    if (user && typeof user === 'object') {
      setDisplayName(user.display_name || '');
      setHandle(user.handle || '');
      setBio(user.bio || '');
      setCategories(user.categories || []);
      setAvatar(user.avatar || null);
      setPhoneCountry(user.phone_country_code || '');
      setPhoneNumber(user.phone_number || '');
    }
  }, [user]);

  // Debounced handle availability check
  useEffect(() => {
    if (!handle) {
      setHandleCheck({ state: 'idle', message: '' });
      return;
    }
    const clean = handle.trim().toLowerCase().replace(/^@/, '');
    if (!/^[a-z0-9_]{3,20}$/.test(clean)) {
      setHandleCheck({ state: 'error', message: '3–20 chars: a–z, 0–9, underscore.' });
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get('/profile/check-handle', { params: { handle: clean } });
        if (data.available) {
          setHandleCheck({ state: 'success', message: `@${clean} is available — this becomes your Bit's ticker.` });
        } else {
          setHandleCheck({ state: 'error', message: data.reason || 'That handle is taken.' });
        }
      } catch (ex) {
        const status = ex?.response?.status;
        if (status === 401) {
          setHandleCheck({
            state: 'error',
            message: 'Your session has expired. Please sign in again to continue.',
          });
        } else {
          setHandleCheck({
            state: 'error',
            message: 'Could not check handle availability. Please check your connection and try again.',
          });
        }
      }
    }, 350);
    return () => clearTimeout(t);
  }, [handle]);

  const toggleCategory = (key) => {
    setCategories((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      if (prev.length >= 3) return prev;
      return [...prev, key];
    });
  };

  const onAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setErr('Please choose an image under 2MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setAvatar(reader.result);
    reader.readAsDataURL(file);
  };

  const canSubmit =
    displayName.trim().length >= 1 &&
    handleCheck.state === 'success' &&
    bio.length <= 160 &&
    !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setErr('');
    try {
      const clean = handle.trim().toLowerCase().replace(/^@/, '');
      await api.put('/profile', {
        display_name: displayName.trim(),
        handle: clean,
        bio,
        categories,
        avatar,
        // Phone (optional). Send empty strings when the user chose to
        // skip so a first-time profile save doesn't preserve stale
        // form-only defaults.
        phone_country_code: phoneCountry || '',
        phone_number: phoneNumber.trim() || '',
      });
      await refreshMe();
      navigate('/home', { replace: true });
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || 'Could not save your profile.');
      setBusy(false);
    }
  };

  return (
    <>
      <BlobField />
      <ImpersonationBanner />
      <div className="stage">
        <div className="card" data-testid="profile-setup-card">
          <div className="top-bar">
            <Link to="/onboarding" className="back-btn" aria-label="Back" data-testid="profile-back-btn">
              <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
                <path d="M15 4l-8 8 8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
          </div>

          <h1 className="heading" style={{ textAlign: 'left' }}>
            Set up your <span className="grad">profile</span>
          </h1>
          <p className="subcopy" style={{ textAlign: 'left', margin: '6px 0 24px' }}>
            This is how other members will find and recognize you across Clanster.
          </p>

          <div className="avatar-row">
            <label className="avatar-upload" data-testid="avatar-upload-label">
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={onAvatarChange} data-testid="avatar-file-input" />
              <div className="avatar-ring">
                <div className="avatar-inner">
                  {avatar ? (
                    <img src={avatar} alt="Your avatar" />
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" width="26" height="26">
                      <path d="M4 20c0-4.4 3.6-7 8-7s8 2.6 8 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.8" />
                    </svg>
                  )}
                </div>
              </div>
              <div className="avatar-cam">
                <svg viewBox="0 0 24 24" fill="none" width="12" height="12">
                  <path d="M4 8h3l2-2h6l2 2h3v11H4V8z" stroke="#fff" strokeWidth="1.8" strokeLinejoin="round" />
                  <circle cx="12" cy="13" r="3" stroke="#fff" strokeWidth="1.8" />
                </svg>
              </div>
            </label>
            <p className="avatar-copy">
              <b>Add a profile photo</b><br />JPG or PNG, at least 400×400px.
            </p>
          </div>

          <div className="field">
            <div className="field-label"><span>Display name</span></div>
            <input
              className="text-input"
              type="text"
              placeholder="e.g. Maya Chen"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value.slice(0, 40))}
              data-testid="display-name-input"
            />
          </div>

          <div className="field">
            <div className="field-label">
              <span>Username</span>
              <span className="char-count">{handle.replace(/^@/, '').length} / 20</span>
            </div>
            <div className="input-wrap">
              <span className="input-prefix">@</span>
              <input
                className="text-input with-prefix"
                type="text"
                placeholder="yourhandle"
                value={handle.replace(/^@/, '')}
                onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 20))}
                data-testid="handle-input"
              />
            </div>
            <div
              className={`validation-row ${handleCheck.state === 'success' ? 'success' : handleCheck.state === 'error' ? 'error' : ''}`}
              data-testid="handle-validation"
            >
              {handleCheck.state === 'success' && (
                <svg viewBox="0 0 16 16" fill="none" width="13" height="13">
                  <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M5.5 8l1.8 1.8L11 6.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
              <span>{handleCheck.message}</span>
            </div>
          </div>

          <div className="field">
            <div className="field-label">
              <span>Short bio</span>
              <span className="char-count">{bio.length} / 160</span>
            </div>
            <textarea
              className="text-input"
              placeholder="Tell your future Clan what you're about…"
              value={bio}
              onChange={(e) => setBio(e.target.value.slice(0, 160))}
              data-testid="bio-input"
            />
          </div>

          {/* Phone capture — Feb 2026. Country dropdown + free-text
              number field. Neither is required; empty submissions are
              accepted server-side. No OTP, no verification. Kept in a
              two-col grid so users see both at once without scrolling. */}
          <div className="field">
            <div className="field-label"><span>Country & phone</span></div>
            <div className="phone-row">
              <select
                className="text-input phone-country-select"
                value={phoneCountry}
                onChange={(e) => setPhoneCountry(e.target.value)}
                data-testid="phone-country-select"
                aria-label="Country"
              >
                <option value="">Select country</option>
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {countryFlag(c.code)} {c.name} ({c.dial})
                  </option>
                ))}
              </select>
              <input
                className="text-input phone-number-input"
                type="tel"
                inputMode="tel"
                placeholder={
                  phoneCountry && COUNTRY_BY_CODE[phoneCountry]
                    ? `${COUNTRY_BY_CODE[phoneCountry].dial} 55 123 4567`
                    : 'Phone number'
                }
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.slice(0, 32))}
                data-testid="phone-number-input"
              />
            </div>
          </div>

          <span className="chip-field-label">
            What best describes you? <span className="light">(pick up to 3)</span>
          </span>
          <div className="chip-row" data-testid="category-chips">
            {CATEGORIES.map((c) => (
              <div
                key={c.key}
                className={`chip ${c.color}${categories.includes(c.key) ? ' selected' : ''}`}
                onClick={() => toggleCategory(c.key)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCategory(c.key); } }}
                data-testid={`category-chip-${c.key.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {c.label || c.key}
              </div>
            ))}
          </div>

          {err && (
            <p style={{ color: 'var(--error)', fontSize: 13, margin: '0 0 12px', fontWeight: 700 }} data-testid="profile-error">
              {err}
            </p>
          )}

          <button className="btn-primary" onClick={submit} disabled={!canSubmit} data-testid="profile-continue-btn">
            <span>{busy ? 'Saving…' : 'Continue'}</span>
            <svg viewBox="0 0 16 16" fill="none">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <p className="signup-note" style={{ marginTop: 16, fontSize: 13 }}>
            You can always change this later in Settings.
          </p>
        </div>
      </div>
    </>
  );
}
